import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext({
  theme: "dark",
  setTheme: () => {},
  toggleTheme: () => {},
  isAdminRoute: false,
});

// Inner provider that has access to useLocation (must be inside BrowserRouter)
function ThemeProviderInner({ children, storageKey = "compass-admin-theme" }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  
  const [adminTheme, setAdminTheme] = useState(() => {
    // Only read from localStorage for admin theme preference
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored === "light" || stored === "dark") {
        return stored;
      }
    }
    return "dark";
  });

  // Effective theme: admin routes use stored preference, all other routes force dark
  const effectiveTheme = isAdminRoute ? adminTheme : "dark";

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove both classes first
    root.classList.remove("light", "dark");
    
    // Add the effective theme class
    root.classList.add(effectiveTheme);
    
    // Only store preference when on admin routes
    if (isAdminRoute) {
      localStorage.setItem(storageKey, adminTheme);
    }
  }, [effectiveTheme, adminTheme, isAdminRoute, storageKey]);

  const setTheme = (newTheme) => {
    // Only allow theme changes on admin routes
    if (isAdminRoute) {
      setAdminTheme(newTheme);
    }
  };

  const toggleTheme = () => {
    // Only allow theme toggle on admin routes
    if (isAdminRoute) {
      setAdminTheme((prev) => (prev === "dark" ? "light" : "dark"));
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: effectiveTheme, 
      setTheme, 
      toggleTheme,
      isAdminRoute 
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Wrapper that can be used at App level (doesn't need BrowserRouter context)
export function ThemeProvider({ children }) {
  return children;
}

// The actual provider to use inside BrowserRouter
export function ThemeProviderWithRouter({ children }) {
  return (
    <ThemeProviderInner>
      {children}
    </ThemeProviderInner>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
